import { useMemo } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";

const newId = () => `knowledge-${Date.now().toString(36)}-${Math.floor(Math.random() * 10000)}`;

function readKnowledge(rawContent) {
  const content = rawContent || {};
  const table = content.table && typeof content.table === "object" ? content.table : {};
  const columns = Array.isArray(table.columns) && table.columns.length >= 2
    ? table.columns.map((column) => String(column ?? ""))
    : ["Параметр", "Вариант A", "Вариант B"];
  const rows = Array.isArray(table.rows)
    ? table.rows.map((row) => ({
        id: row?.id || newId(),
        cells: Array.from({ length: columns.length }, (_, index) => String(row?.cells?.[index] ?? "")),
      }))
    : [];
  return {
    ...table,
    eyebrow: table.eyebrow ?? content.eyebrow ?? "",
    title: table.title ?? content.headline ?? "",
    introduction: table.introduction ?? content.lead ?? "",
    note: table.note ?? content.caption ?? "",
    columns,
    rows,
  };
}

export function KnowledgeEditor({ block, onPatch }) {
  const knowledge = useMemo(() => readKnowledge(block.content), [block.content]);
  const patch = (delta) => onPatch({
    content: {
      ...(block.content || {}),
      table: { ...knowledge, ...delta },
    },
  });

  const setColumns = (columns) => patch({
    columns,
    rows: knowledge.rows.map((row) => ({
      ...row,
      cells: Array.from({ length: columns.length }, (_, index) => row.cells[index] ?? ""),
    })),
  });
  const addColumn = () => {
    if (knowledge.columns.length < 4) setColumns([...knowledge.columns, "Новый столбец"]);
  };
  const removeColumn = (index) => {
    if (knowledge.columns.length <= 2) return;
    setColumns(knowledge.columns.filter((_, columnIndex) => columnIndex !== index));
  };
  const patchColumn = (index, value) => {
    const columns = [...knowledge.columns];
    columns[index] = value;
    setColumns(columns);
  };

  const setRows = (rows) => patch({ rows });
  const addRow = () => setRows([
    ...knowledge.rows,
    { id: newId(), cells: knowledge.columns.map(() => "") },
  ]);
  const patchCell = (rowIndex, columnIndex, value) => {
    const rows = knowledge.rows.map((row, index) => {
      if (index !== rowIndex) return row;
      const cells = [...row.cells];
      cells[columnIndex] = value;
      return { ...row, cells };
    });
    setRows(rows);
  };
  const moveRow = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= knowledge.rows.length) return;
    const rows = [...knowledge.rows];
    [rows[index], rows[target]] = [rows[target], rows[index]];
    setRows(rows);
  };
  const removeRow = (index) => setRows(knowledge.rows.filter((_, rowIndex) => rowIndex !== index));

  return (
    <div className="space-y-4" data-testid="knowledge-editor">
      <div className="rounded-2xl border hairline bg-white p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest opacity-70">Знание · справочный разворот</div>
        <Input value={knowledge.eyebrow}
               onChange={(event) => patch({ eyebrow: event.target.value })}
               placeholder="СПРАВОЧНИК · необязательно"
               data-testid="knowledge-eyebrow" />
        <Input value={knowledge.title}
               onChange={(event) => patch({ title: event.target.value })}
               className="font-serif text-lg"
               placeholder="Заголовок"
               data-testid="knowledge-title" />
        <textarea rows={3} value={knowledge.introduction}
                  onChange={(event) => patch({ introduction: event.target.value })}
                  className="w-full rounded border px-3 py-2 text-sm font-serif leading-relaxed"
                  placeholder="Короткое пояснение · необязательно"
                  data-testid="knowledge-introduction" />
      </div>

      <div className="rounded-2xl border hairline bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-widest opacity-70">Таблица</div>
          <div className="flex items-center gap-3 text-xs">
            <button type="button" onClick={addColumn} disabled={knowledge.columns.length >= 4}
                    className="inline-flex items-center gap-1 disabled:opacity-35"
                    data-testid="knowledge-add-column">
              <Plus className="h-3.5 w-3.5" /> Столбец
            </button>
            <button type="button" onClick={addRow} className="inline-flex items-center gap-1"
                    data-testid="knowledge-add-row">
              <Plus className="h-3.5 w-3.5" /> Строка
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[620px] space-y-2">
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${knowledge.columns.length}, minmax(0, 1fr)) 88px` }}>
              {knowledge.columns.map((column, index) => (
                <div key={`column-${index}`} className="flex items-center gap-1">
                  <Input value={column} onChange={(event) => patchColumn(index, event.target.value)}
                         data-testid={`knowledge-column-${index}`} />
                  <button type="button" onClick={() => removeColumn(index)}
                          disabled={knowledge.columns.length <= 2}
                          className="p-1 text-red-600 opacity-60 disabled:opacity-20"
                          title="Удалить столбец">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <div />
            </div>

            {knowledge.rows.length === 0 && (
              <div className="rounded-lg border border-dashed border-espresso/25 bg-cream/50 p-5 text-center text-xs opacity-65">
                Добавьте первую строку справочной таблицы.
              </div>
            )}
            {knowledge.rows.map((row, rowIndex) => (
              <div key={row.id} className="grid gap-2 rounded-lg bg-cream/35 p-2"
                   style={{ gridTemplateColumns: `repeat(${knowledge.columns.length}, minmax(0, 1fr)) 88px` }}
                   data-testid={`knowledge-row-${rowIndex}`}>
                {knowledge.columns.map((_, columnIndex) => (
                  <textarea key={`${row.id}-${columnIndex}`} rows={2}
                            value={row.cells[columnIndex] || ""}
                            onChange={(event) => patchCell(rowIndex, columnIndex, event.target.value)}
                            className="w-full rounded border px-2 py-1.5 text-sm"
                            data-testid={`knowledge-cell-${rowIndex}-${columnIndex}`} />
                ))}
                <div className="flex items-center justify-end gap-1">
                  <button type="button" onClick={() => moveRow(rowIndex, -1)} disabled={rowIndex === 0}
                          className="p-1 opacity-60 disabled:opacity-20" title="Выше">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => moveRow(rowIndex, 1)} disabled={rowIndex === knowledge.rows.length - 1}
                          className="p-1 opacity-60 disabled:opacity-20" title="Ниже">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={() => removeRow(rowIndex)}
                          className="p-1 text-red-600 opacity-60" title="Удалить">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Input value={knowledge.note}
               onChange={(event) => patch({ note: event.target.value })}
               placeholder="Примечание · необязательно"
               data-testid="knowledge-note" />
      </div>
    </div>
  );
}

export function KnowledgeRender({ block, mode }) {
  const knowledge = readKnowledge(block.content);
  const isMobile = mode === "mobile";
  const rows = knowledge.rows.filter((row) => row.cells.some((cell) => String(cell || "").trim()));
  return (
    <section className={isMobile ? "overflow-hidden px-6 py-10" : "overflow-hidden px-10 py-14"}
             data-testid="knowledge-render">
      <div className="mx-auto max-w-[1200px]">
        {knowledge.eyebrow && (
          <div className="mb-2 text-[10px] uppercase tracking-[0.25em] opacity-55">{knowledge.eyebrow}</div>
        )}
        {knowledge.title && (
          <h2 className={`${isMobile ? "text-[26px]" : "text-[40px]"} mb-3 break-words font-serif leading-tight text-espresso [overflow-wrap:anywhere]`}>
            {knowledge.title}
          </h2>
        )}
        {knowledge.introduction && (
          <p className="mb-7 max-w-3xl font-serif leading-relaxed opacity-75">{knowledge.introduction}</p>
        )}
        {rows.length > 0 && (isMobile ? (
          <div className="space-y-4" data-testid="knowledge-mobile-cards">
            {rows.map((row) => (
              <dl key={row.id} className="divide-y divide-espresso/10 border-y border-espresso/15">
                {knowledge.columns.map((column, index) => (
                  <div key={`${row.id}-${index}`} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 py-3">
                    <dt className="text-[10px] uppercase tracking-[0.16em] opacity-60">{column || "—"}</dt>
                    <dd className={`${index === 0 ? "font-medium" : ""} min-w-0 whitespace-pre-wrap break-words font-serif text-[14px] leading-relaxed`}>
                      {row.cells[index] || "—"}
                    </dd>
                  </div>
                ))}
              </dl>
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-espresso/15">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-cream/80">
              <tr>
                {knowledge.columns.map((column, index) => (
                  <th key={`head-${index}`} className="border-b border-espresso/15 px-4 py-3 font-medium">
                    {column || "—"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-espresso/10 last:border-0">
                  {knowledge.columns.map((_, index) => (
                    <td key={`${row.id}-${index}`} className={`${index === 0 ? "font-medium" : ""} px-4 py-3 align-top whitespace-pre-wrap`}>
                      {row.cells[index] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ))}
        {knowledge.note && <div className="mt-3 text-xs italic opacity-60">{knowledge.note}</div>}
      </div>
    </section>
  );
}
